import * as Sentry from '@sentry/react';
import React from 'react';

export type ArtifactKind =
  | 'specstream'
  | 'jsx'
  | 'jsx-render'
  | 'html'
  | 'notebook'
  | 'table'
  | 'pdf'
  | 'text';

// Captures an artifact-related error with consistent tags + extras so we can
// filter in Sentry by artifact_kind and correlate to a specific artifact URL.
export function captureArtifactError (
  error: unknown,
  ctx: { url?: string; kind: ArtifactKind; extra?: Record<string, unknown> },
) {
  Sentry.captureException(error, {
    tags: { artifact_kind: ctx.kind },
    extra: { artifact_url: ctx.url, ...ctx.extra },
  });
}

type ArtifactErrorBoundaryProps = {
  kind: ArtifactKind;
  url?: string;
  children: React.ReactNode;
};

// Per-artifact error boundary. Catches render-time crashes inside one artifact
// (so a bad spec/JSX dashboard doesn't unmount the entire chat tree) and
// tags the captured event with artifact_kind + artifact_url.
export const ArtifactErrorBoundary = ({ kind, url, children }: ArtifactErrorBoundaryProps) => (
  <Sentry.ErrorBoundary
    beforeCapture={(scope) => {
      scope.setTag('artifact_kind', kind);
      scope.setExtra('artifact_url', url);
    }}
    fallback={
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        Failed to render this artifact.
      </div>
    }
  >
    {children}
  </Sentry.ErrorBoundary>
);
